/* The tenant's own public website: what is live, and where.
 *
 * Frappe already has the builder — Web Pages, Web Forms, a theme — and the
 * metadata engine already draws every one of those forms, so none of that is
 * rebuilt here. What a generic list cannot do, because it has no idea what the
 * rows mean, is answer the only two questions anybody has about a website:
 * **which of these is live, and what is the address?** A `published` checkbox
 * in a column is not that answer.
 *
 * Web forms get a third question, which is the one that matters: a form writes
 * into a DocType, so "how many have come in, and where did they go" turns
 * "we have a contact form" into "the contact form is working".
 */
import { useCallback, useEffect, useState } from 'react'
import { useErp } from '@jackdesk/erp-kit'
import type { WebsiteAnswer, WebThing } from '@jackdesk/erp-kit'
import { EmptyFirstTime, Failed, Loading } from '@jackdesk/shell'

export function Website({ onOpen, onOpenList }: {
  onOpen: (doctype: string, name: string) => void
  onOpenList: (doctype: string) => void
}) {
  const erp = useErp()
  const [answer, setAnswer] = useState<WebsiteAnswer | null>(null)
  const [failed, setFailed] = useState(false)

  const load = useCallback(() => {
    const controller = new AbortController()
    setAnswer(null); setFailed(false)
    erp.website(controller.signal)
      .then(setAnswer)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed(true) })
    return () => controller.abort()
  }, [erp])

  useEffect(load, [load])

  if (failed) return <div className="page"><Failed onRetry={load} /></div>
  if (!answer) return <div className="page"><Loading rows={6} label="Loading your website" /></div>

  return (
    <div className="page" style={{ display: 'grid', gap: 22 }}>
      <div>
        <h1>Your website</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          Pages and forms anybody can reach without signing in. A form writes
          straight into your records, so what people send arrives where the rest
          of your work already is.
        </p>
        {!answer.address_prefix ? (
          <p className="small muted" style={{ marginTop: 8 }}>
            {/* Better to say this than to print a link that 404s. */}
            No domain is set for your site yet, so addresses are shown as paths.
            Set one in Website Settings.
          </p>
        ) : null}
      </div>

      <Section
        title="Pages" what="pages"
        rows={answer.pages} doctype="Web Page"
        onOpen={onOpen} onOpenList={onOpenList}
      />

      <Section
        title="Forms" what="forms"
        rows={answer.forms} doctype="Web Form"
        onOpen={onOpen} onOpenList={onOpenList}
      />

      <section>
        <h2 style={{ marginBottom: 8 }}>Selling online</h2>
        {answer.shop.available ? (
          <p className="small muted">Your online shop is switched on.</p>
        ) : (
          <div className="card card-pad">
            <p className="small" style={{ margin: 0 }}>{answer.shop.why}</p>
            <p className="small muted" style={{ marginTop: 6, marginBottom: 0 }}>
              Everything else on this screen works without it. A catalogue and a
              basket need that app installed on your site first.
            </p>
          </div>
        )}
      </section>
    </div>
  )
}

function Section({ title, what, rows, doctype, onOpen, onOpenList }: {
  title: string
  what: string
  rows: WebThing[]
  doctype: string
  onOpen: (doctype: string, name: string) => void
  onOpenList: (doctype: string) => void
}) {
  return (
    <section>
      <div className="row-between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-sm" onClick={() => onOpenList(doctype)}>All {what}</button>
          <button className="btn btn-primary btn-sm" onClick={() => onOpen(doctype, 'new')}>
            New
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyFirstTime what={what}
                        action={<button className="btn btn-primary"
                                        onClick={() => onOpen(doctype, 'new')}>Create the first one</button>} />
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {rows.map((row) => (
            <div key={row.name} className="navitem" style={{ alignItems: 'center' }}
                 onClick={() => onOpen(doctype, row.name)}>
              <span className="grow truncate" style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{row.title}</span>
                <span className="small muted"> · {row.address || 'no address yet'}</span>
                {row.doctype ? (
                  <span className="small muted"> · into {row.doctype}</span>
                ) : null}
              </span>

              {/* A count of nothing and "we may not look" are different facts. */}
              {row.doctype ? (
                <span className="small muted" style={{ flex: 'none' }}>
                  {row.received === null || row.received === undefined
                    ? '—'
                    : `${row.received.toLocaleString('en-IN')} in`}
                </span>
              ) : null}

              <span className={`chip ${row.published ? 'chip-ok' : ''}`} style={{ flex: 'none' }}>
                {row.published ? 'Live' : 'Draft'}
              </span>

              {row.published && row.address ? (
                <a className="btn btn-quiet btn-sm" style={{ flex: 'none' }}
                   href={row.address} target="_blank" rel="noopener noreferrer"
                   onClick={(e) => e.stopPropagation()}>View</a>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
